import React, { useState } from 'react';
import { Bot, Target, X, ChevronDown } from 'lucide-react';
import { useStore } from '../../../store/useStore';
import { MINING_DATA } from '../../../data/mining';
import { ItemIcon } from '../../common/ItemIcon';
import type { Resource, DroneState } from '../../../types';

interface DroneCardProps {
    drone: DroneState;
    onSelectTarget: (droneId: string) => void;
    isSelecting: boolean;
    minimal?: boolean; // Use minimal list style
}

const DroneCard: React.FC<DroneCardProps> = ({ drone, onSelectTarget, isSelecting, minimal = false }) => {
    const skills = useStore((state) => state.skills);

    // Get ore info if assigned
    const oreData = drone.targetOre ? MINING_DATA.find(m => m.id === drone.targetOre) : null;

    // Drone tier colors
    const tierColors: Record<number, string> = {
        1: '#f59e0b', // Copper - Amber
        2: '#94a3b8', // Iron - Slate
        3: '#60a5fa', // Mithril - Blue
        4: '#22c55e', // Adamant - Green
        5: '#3b82f6', // Rune - Indigo
    };

    const tierNames: Record<number, string> = {
        1: 'Tier I',
        2: 'Tier II',
        3: 'Tier III',
        4: 'Tier IV',
        5: 'Tier V',
    };

    const tierColor = tierColors[drone.tier] || '#94a3b8';
    const tierName = tierNames[drone.tier] || 'Unknown';

    // Calculate estimated time per ore
    const droneSpeed = oreData ? Math.max(5000, (oreData.baseCooldown || 500) * 10) : 0;
    const estimatedTimeSeconds = droneSpeed / 1000;

    if (minimal) {
        return (
            <div
                onClick={() => onSelectTarget(drone.id)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: isSelecting ? '1px solid #ef4444' : '1px solid var(--color-border)',
                    background: isSelecting ? 'rgba(239, 68, 68, 0.1)' : 'var(--color-bg-panel-hover)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                }}
            >
                {/* Icon Box */}
                <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: `${tierColor}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: `1px solid ${tierColor}40`
                }}>
                    <Bot size={20} color={tierColor} />
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                            {tierName}
                        </span>
                        {oreData && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                                {Math.floor(drone.progress)}%
                            </span>
                        )}
                    </div>

                    {oreData ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <ItemIcon id={drone.targetOre!} size={16} />
                            </div>
                            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {oreData.name}
                            </span>
                        </div>
                    ) : (
                        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                            Idle
                        </span>
                    )}

                    {/* Mini Progress Bar */}
                    {oreData && (
                        <div style={{
                            height: '4px',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '2px',
                            marginTop: '6px',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                height: '100%',
                                width: `${drone.progress}%`,
                                background: tierColor,
                                borderRadius: '2px',
                                transition: 'width 0.1s linear'
                            }} />
                        </div>
                    )}
                </div>

                {isSelecting && <ChevronDown size={16} color="#ef4444" />}

            </div>
        );
    }

    // Existing Card Layout for Horizontal/Grid view
    return (
        <div
            onClick={() => onSelectTarget(drone.id)}
            style={{
                background: isSelecting
                    ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(185, 28, 28, 0.2) 100%)'
                    : 'var(--color-bg-panel-hover)',
                border: isSelecting
                    ? '2px solid #ef4444'
                    : `2px solid ${tierColor}`,
                borderRadius: '12px',
                padding: '16px',
                minWidth: '160px',
                maxWidth: '200px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: isSelecting
                    ? '0 0 20px rgba(239, 68, 68, 0.3)'
                    : `0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0px rgba(255, 255, 255, 0.05)`,
                transform: isSelecting ? 'scale(1.02)' : 'scale(1)',
            }}
        >
            {/* Header: Drone Tier */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
            }}>
                <Bot size={20} color={tierColor} />
                <span style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: tierColor,
                }}>
                    {tierName} Drone
                </span>
            </div>

            {/* Target Display */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                minHeight: '80px',
            }}>
                {drone.targetOre && oreData ? (
                    <>
                        <ItemIcon id={drone.targetOre} size={32} />
                        <span style={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: 'var(--color-text-primary)',
                        }}>
                            {oreData.name}
                        </span>
                    </>
                ) : (
                    <>
                        <Target size={32} color="var(--color-text-muted)" />
                        <span style={{
                            fontSize: '0.875rem',
                            color: 'var(--color-text-muted)',
                        }}>
                            No Target
                        </span>
                    </>
                )}
            </div>

            {/* Progress Bar */}
            {drone.targetOre && (
                <div style={{ marginTop: '12px' }}>
                    <div style={{
                        height: '8px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: '4px',
                        overflow: 'hidden',
                    }}>
                        <div
                            style={{
                                height: '100%',
                                width: `${drone.progress}%`,
                                background: `linear-gradient(90deg, ${tierColor} 0%, ${tierColor}aa 100%)`,
                                borderRadius: '4px',
                                transition: 'width 0.1s linear',
                            }}
                        />
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '4px',
                        fontSize: '0.7rem',
                        color: 'var(--color-text-muted)',
                    }}>
                        <span>{Math.floor(drone.progress)}%</span>
                        <span>~{estimatedTimeSeconds.toFixed(0)}s/ore</span>
                    </div>
                </div>
            )}

            {/* Click hint */}
            <div style={{
                marginTop: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                fontSize: '0.7rem',
                color: isSelecting ? '#ef4444' : 'var(--color-text-muted)',
            }}>
                {isSelecting ? (
                    <>
                        <X size={12} />
                        Click to cancel
                    </>
                ) : (
                    <>
                        <ChevronDown size={12} />
                        Click to assign
                    </>
                )}
            </div>
        </div>
    );
};

interface DronePanelProps {
    vertical?: boolean;
}

export const DronePanel: React.FC<DronePanelProps> = ({ vertical = false }) => {
    const drones = useStore((state) => state.drones);
    const setDroneTarget = useStore((state) => state.setDroneTarget);
    const skills = useStore((state) => state.skills);

    const [selectingDroneId, setSelectingDroneId] = useState<string | null>(null);

    const miningLevel = skills.mining?.level ?? 1;

    // If no drones unlocked, don't render
    if (drones.activeDrones.length === 0) {
        return null; // Or return a placeholder if we want to keep layout stable, but usually null is fine.
    }

    // Get drone being configured
    const selectingDrone = selectingDroneId
        ? drones.activeDrones.find(d => d.id === selectingDroneId)
        : null;

    // Get ores this drone can mine (tier <= drone.tier AND level <= player level)
    const availableOres = selectingDrone
        ? MINING_DATA.filter(ore =>
            ore.tier <= selectingDrone.tier && ore.level <= miningLevel
        )
        : [];

    const handleDroneClick = (droneId: string) => {
        if (selectingDroneId === droneId) {
            // Toggle off
            setSelectingDroneId(null);
        } else {
            setSelectingDroneId(droneId);
        }
    };

    const handleOreSelect = (ore: Resource) => {
        if (selectingDroneId) {
            setDroneTarget(selectingDroneId, ore);
            setSelectingDroneId(null);
        }
    };

    const handleClearTarget = () => {
        if (selectingDroneId) {
            setDroneTarget(selectingDroneId, null);
            setSelectingDroneId(null);
        }
    };

    return (
        <div style={{
            background: 'var(--color-bg-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: vertical ? 0 : '24px', // Remove margin bottom if vertical as it's likely in a side column
            height: vertical ? '100%' : 'auto',
            display: 'flex',
            flexDirection: 'column',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    <Bot size={24} color="var(--color-skill-mining)" />
                    <h3 style={{
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)',
                        margin: 0,
                    }}>
                        Mining Drones
                    </h3>
                </div>
                <span style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                }}>
                    {drones.activeDrones.filter(d => d.targetOre).length}/{drones.activeDrones.length} Active
                </span>
            </div>

            {/* Drone Cards */}
            <div style={{
                display: 'flex',
                flexWrap: vertical ? 'nowrap' : 'wrap',
                flexDirection: vertical ? 'column' : 'row',
                gap: '12px', // Reduce gap for vertical
                overflowY: vertical ? 'auto' : 'visible',
            }}>
                {drones.activeDrones.map(drone => (
                    <div key={drone.id} style={{ width: vertical ? '100%' : 'auto' }}>
                        <DroneCard
                            drone={drone}
                            onSelectTarget={handleDroneClick}
                            isSelecting={selectingDroneId === drone.id}
                            minimal={vertical}
                        />
                    </div>
                ))}
            </div>

            {/* Ore Picker (shows when selecting) */}
            {selectingDroneId && selectingDrone && (
                <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                }}>

                    <div style={{
                        display: 'flex',
                        flexDirection: 'column', // Stack vertically for narrow columns
                        gap: '8px',
                        marginBottom: '12px',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: 'var(--color-text-primary)',
                                lineHeight: '1.2'
                            }}>
                                Select Target Ore
                            </span>
                            <button
                                onClick={handleClearTarget}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '4px 8px',
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                    borderRadius: '4px',
                                    color: '#ef4444',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <X size={12} />
                                Clear
                            </button>
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                            Available for Tier {selectingDrone.tier} and below
                        </span>
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                        gap: '8px',
                    }}>
                        {availableOres.map(ore => {
                            const isSelected = selectingDrone.targetOre === ore.id;

                            return (
                                <button
                                    key={ore.id}
                                    onClick={() => handleOreSelect(ore.id)}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '12px 8px',
                                        background: isSelected
                                            ? 'rgba(255, 255, 255, 0.05)' // Subtle highlight
                                            : 'transparent',
                                        border: isSelected
                                            ? '2px solid var(--color-primary)' // Solid primary border
                                            : '1px solid var(--color-border)',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        opacity: isSelected ? 1 : 0.7 // Slight dim for unselected
                                    }}
                                >
                                    {isSelected && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '4px',
                                            right: '4px',
                                            background: 'var(--color-primary)',
                                            borderRadius: '50%',
                                            width: '16px',
                                            height: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 10
                                        }}>
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        </div>
                                    )}

                                    {/* Active Helper text/icon if needed, or just glow */}
                                    <div style={{ position: 'relative', zIndex: 1 }}>
                                        <ItemIcon id={ore.id} size={28} />
                                    </div>
                                    <span style={{
                                        fontSize: '0.75rem',
                                        color: isSelected ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                                        fontWeight: isSelected ? 600 : 400,
                                        textAlign: 'center',
                                    }}>
                                        {ore.name}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
